import { HomeReview, ReviewContactDetails, ReviewTemplate } from '../types';
import { cloneTemplate } from '../utils/cloneTemplate';
import { OfflineReview } from './offlineDatabase';
import { CreateInitialResponsesInput } from './offlineSharedPrefillService';
import { mapOfflineReviewToHomeReview } from './reviewOfflineTransforms';

export interface OfflineReviewCreationDatabase {
  getCachedTemplates(): Promise<ReviewTemplate[]>;
  createReview(
    data: Omit<OfflineReview, 'id' | 'tempId' | 'syncStatus' | 'createdAt' | 'updatedAt'>
  ): Promise<OfflineReview>;
}

export interface OfflineReviewCreationTemplateCache {
  getTemplates(): Promise<ReviewTemplate[]>;
}

export interface OfflineReviewCreationAddressCache {
  getLatestAddressForProperty(propertyId: number): Promise<string | null>;
}

export interface OfflineReviewCreationPrefillService {
  createInitialResponses(input: CreateInitialResponsesInput): Promise<void>;
}

export interface OfflineReviewCreationCallbacks {
  registerPendingReviewTempId(tempId: string): void;
  enqueueReviewCreate(reviewId: number, tempId?: string): void;
}

export interface OfflineReviewCreationDependencies {
  addressCache: OfflineReviewCreationAddressCache;
  callbacks: OfflineReviewCreationCallbacks;
  database: OfflineReviewCreationDatabase;
  getOfflineReview?: (reviewId: number) => Promise<HomeReview>;
  prefillService: OfflineReviewCreationPrefillService;
  templateCache: OfflineReviewCreationTemplateCache;
}

export class OfflineReviewCreationService {
  constructor(private readonly dependencies: OfflineReviewCreationDependencies) {}

  async createOfflineReview(
    propertyId: number,
    templateId: number,
    address?: string,
    details?: ReviewContactDetails
  ): Promise<HomeReview> {
    try {
      const template = await this.getTemplate(templateId);

      if (!template) {
        throw new Error(`Template ${templateId} not available offline. Your cache may have expired. Please connect to the internet to refresh templates.`);
      }

      const reviewAddress = address || await this.dependencies.addressCache.getLatestAddressForProperty(propertyId);
      const offlineReview = await this.dependencies.database.createReview({
        propertyId,
        templateId,
        reviewerId: 1,
        status: 'draft',
        startedAt: new Date(),
        address: reviewAddress || undefined,
        memberName: details?.memberName,
        memberEmail: details?.memberEmail,
        homeManagerName: details?.homeManagerName,
        homeManagerEmail: details?.homeManagerEmail,
        notes: undefined,
        onDemandProjects: undefined,
      });

      if (offlineReview.tempId) {
        this.dependencies.callbacks.registerPendingReviewTempId(offlineReview.tempId);
      }

      const templateCopy = cloneTemplate(template);

      await this.dependencies.prefillService.createInitialResponses({
        propertyId,
        templateId,
        template: templateCopy,
        offlineReview,
      });

      this.dependencies.callbacks.enqueueReviewCreate(offlineReview.id, offlineReview.tempId);

      if (this.dependencies.getOfflineReview) {
        return await this.dependencies.getOfflineReview(offlineReview.id);
      }

      return mapOfflineReviewToHomeReview(offlineReview, templateCopy);
    } catch (error) {
      console.error('[OfflineReviewService] Error creating offline review:', error);
      throw error;
    }
  }

  private async getTemplate(templateId: number): Promise<ReviewTemplate | undefined> {
    const templates = await this.dependencies.templateCache.getTemplates();
    let template = templates.find(candidate => candidate.id === templateId);

    if (!template || !template.categories || template.categories.length === 0) {
      try {
        const dbTemplates = await this.dependencies.database.getCachedTemplates();
        const dbTemplate = dbTemplates.find(candidate => candidate.id === templateId);

        if (dbTemplate && dbTemplate.categories && dbTemplate.categories.length > 0) {
          template = dbTemplate;
        }
      } catch (error) {
        console.warn('[OfflineReviewService] Fallback template lookup failed:', error);
      }
    }

    return template;
  }
}
